import React, { useEffect, useRef, useState, useCallback } from 'react';

const CustomScrollbar = ({
  scrollbarHeight = 24,
  topGap = 24,
  bottomGap = 24,
}) => {
  // Only used for ARIA + a light visual snap after scrolling/resizing (not during drag)
  const [scrollBarTopState, setScrollBarTopState] = useState(topGap);
  const [dragging, setDragging] = useState(false);

  // DOM + live values (no re-renders needed)
  const thumbRef = useRef(null);
  const posRef = useRef(topGap);
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef(0);
  const frameRef = useRef(null);

  // cached metrics
  const metricsRef = useRef({
    windowHeight: 0,
    docHeight: 0,
    totalHeight: 0,
    availableHeight: 0,
    maxPosition: 0,
  });

  const scroller = () =>
    document.scrollingElement || document.documentElement;

  const setThumbY = (y, { commitState = false } = {}) => {
    posRef.current = y;
    if (thumbRef.current) {
      // round to whole pixels for smoother composites
      thumbRef.current.style.transform = `translateY(${Math.round(y)}px)`;
    }
    if (commitState) setScrollBarTopState(y);
  };

  const computeMetrics = useCallback(() => {
    const windowHeight = window.innerHeight;
    const docHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight
    );
    const totalHeight = Math.max(docHeight - windowHeight, 0);
    const availableHeight = windowHeight - topGap - bottomGap - scrollbarHeight;
    const maxPosition = windowHeight - bottomGap - scrollbarHeight;

    metricsRef.current = {
      windowHeight,
      docHeight,
      totalHeight,
      availableHeight,
      maxPosition,
    };
  }, [topGap, bottomGap, scrollbarHeight]);

  // page scroll -> thumb
  const syncFromPageScroll = useCallback(() => {
    if (isDraggingRef.current) return;

    computeMetrics();
    const { totalHeight, availableHeight, maxPosition } = metricsRef.current;

    if (totalHeight <= 0 || availableHeight <= 0) {
      setThumbY(topGap, { commitState: true });
      return;
    }

    const scrollTop = scroller().scrollTop || 0;
    const scrollPercent = scrollTop / totalHeight;
    const newY = topGap + scrollPercent * availableHeight;
    const constrained = Math.max(topGap, Math.min(newY, maxPosition));

    // update DOM (and a light state update, but only on scroll/resize)
    setThumbY(constrained, { commitState: true });
  }, [computeMetrics, topGap]);

  // throttle scroll/resize with rAF
  const scheduleSync = useCallback(() => {
    if (frameRef.current) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      syncFromPageScroll();
    });
  }, [syncFromPageScroll]);

  useEffect(() => {
    computeMetrics();
    // initial position
    setThumbY(topGap, { commitState: true });
    syncFromPageScroll();

    window.addEventListener('scroll', scheduleSync, { passive: true });
    window.addEventListener('resize', scheduleSync, { passive: true });

    // Observe doc size changes (images, async content)
    const ro = new ResizeObserver(scheduleSync);
    ro.observe(document.documentElement);
    ro.observe(document.body);

    return () => {
      window.removeEventListener('scroll', scheduleSync);
      window.removeEventListener('resize', scheduleSync);
      ro.disconnect();
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [computeMetrics, scheduleSync, syncFromPageScroll, topGap]);

  // thumb -> page scroll
  const setPageScrollFromThumbY = (thumbY) => {
    const { totalHeight, availableHeight } = metricsRef.current;
    if (totalHeight <= 0 || availableHeight <= 0) return;

    const percent = (thumbY - topGap) / availableHeight;
    const newScrollTop = Math.max(0, Math.min(percent * totalHeight, totalHeight));
    scroller().scrollTo({ top: newScrollTop, behavior: 'auto' });
  };

  // Drag handling
  const onPointerDown = (e) => {
    // make sure metrics are fresh at the start
    computeMetrics();

    isDraggingRef.current = true;
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    document.documentElement.style.userSelect = 'none';

    const rect = e.currentTarget.getBoundingClientRect();
    dragOffsetRef.current = e.clientY - rect.top;
    e.preventDefault();
  };

  const onPointerMove = (e) => {
    if (!isDraggingRef.current) return;

    // batch DOM writes in rAF
    if (!frameRef.current) {
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        const { maxPosition } = metricsRef.current;

        const desired = e.clientY - dragOffsetRef.current;
        const constrained = Math.max(topGap, Math.min(desired, maxPosition));

        // no state here: direct DOM + page scroll
        setThumbY(constrained);
        setPageScrollFromThumbY(constrained);
      });
    }
    e.preventDefault();
  };

  const endDrag = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setDragging(false);
    document.documentElement.style.userSelect = '';
    // after dragging, align state once
    setThumbY(posRef.current, { commitState: true });
  };

  useEffect(() => {
    const up = () => endDrag();
    const cancel = () => endDrag();
    const blur = () => endDrag();

    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('blur', blur);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('blur', blur);
    };
  }, []); // stable handlers from closure

  // Keyboard (kept simple and snappy)
  const onKeyDown = (e) => {
    const line = 40;
    const page = window.innerHeight * 0.9;
    switch (e.key) {
      case 'ArrowUp':
        scroller().scrollBy(0, -line);
        e.preventDefault();
        break;
      case 'ArrowDown':
        scroller().scrollBy(0, line);
        e.preventDefault();
        break;
      case 'PageUp':
        scroller().scrollBy(0, -page);
        e.preventDefault();
        break;
      case 'PageDown':
        scroller().scrollBy(0, page);
        e.preventDefault();
        break;
      case 'Home':
        scroller().scrollTo(0, 0);
        e.preventDefault();
        break;
      case 'End': {
        const { totalHeight } = metricsRef.current;
        scroller().scrollTo(0, totalHeight);
        e.preventDefault();
        break;
      }
      default:
        break;
    }
  };

  // ARIA values (derived from live pos)
  const { totalHeight, availableHeight } = metricsRef.current;
  const ariaMax = totalHeight || 1;
  const ariaNow = Math.max(
    0,
    Math.min(((posRef.current - topGap) / (availableHeight || 1)) * ariaMax, ariaMax)
  );

  return (
    <div
      ref={thumbRef}
      className="fixed right-0 w-2.5 h-6 bg-black dark:bg-white z-50 "
      style={{
        top: 0,
        transform: `translateY(${Math.round(scrollBarTopState)}px)`,
        // Smooth only when NOT dragging
        transition: dragging ? 'none' : 'transform 75ms ease-out',
        touchAction: 'none',
        cursor: dragging ? 'grabbing' : 'grab',
        willChange: 'transform', // hint GPU compositing
      }}
      role="scrollbar"
      aria-orientation="vertical"
      aria-valuemin={0}
      aria-valuemax={Math.round(ariaMax)}
      aria-valuenow={Math.round(ariaNow)}
      aria-label="Page scrollbar"
      tabIndex={0}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
    />
  );
};

export default CustomScrollbar;
